import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Lock, Handshake, Globe, ChevronDown, ChevronUp, Check, Tag, Gift, X } from "lucide-react";

/**
 * Friendly visibility picker.
 * Backend contract is UNCHANGED:
 *   - visibility: "internal" | "shared" | "public"
 *   - shared_with: string[]  (empty array = share with ALL collaborations)
 *
 * UX layer:
 *   - 3 large option cards with plain-language names + 1-sentence helper
 *   - "Share with partners" defaults to ALL collaborations (no org-IDs visible)
 *   - Optional "Advanced" toggle reveals a per-org picker for power users
 *   - Live status summary so the user always knows the current effect
 *   - For materials with no price: switching to PUBLIC triggers a small
 *     "Sell or give away?" inline confirmation. Parent supplies onSetPrice.
 */
const OPTIONS = [
  { key: "internal", Icon: Lock,      tint: "var(--ink-200)" },
  { key: "shared",   Icon: Handshake, tint: "var(--moss)"    },
  { key: "public",   Icon: Globe,     tint: "var(--clay)"    },
];

export default function VisibilityPicker({
  resourceType,
  resourceId,
  currentVisibility,
  currentSharedWith,
  currentPrice,           // optional — only set for materials
  onSetPrice,             // optional async fn(newPrice) — parent saves price
  onChange,
}) {
  const { t } = useI18n();
  const [vis, setVis] = useState(currentVisibility || "internal");
  const [sharedWith, setSharedWith] = useState(currentSharedWith || []);
  const [advanced, setAdvanced] = useState(false);
  const [collabs, setCollabs] = useState([]);
  const [saving, setSaving] = useState(false);
  // Sell-or-give flow (materials only) — single panel with always-visible mode toggle
  const [pricePrompt, setPricePrompt] = useState(false);  // panel open?
  const [priceMode, setPriceMode] = useState(null);       // null | "sell" | "giveaway"
  const [priceInput, setPriceInput] = useState("");
  const [priceConfirmed, setPriceConfirmed] = useState(false);

  useEffect(() => { setVis(currentVisibility || "internal"); }, [currentVisibility]);
  useEffect(() => { setSharedWith(currentSharedWith || []); }, [currentSharedWith]);

  useEffect(() => {
    api.get("/org/me/collaborations")
      .then((r) => setCollabs(r.data?.accepted || []))
      .catch(() => {});
  }, []);

  // Open advanced mode automatically if user already restricted shared_with to specific orgs
  useEffect(() => {
    if ((currentSharedWith || []).length > 0) setAdvanced(true);
  }, [currentSharedWith]);

  const summary = useMemo(() => {
    if (vis === "internal") return t("vis.status.internal");
    if (vis === "public")   return t("vis.status.public");
    // shared
    if (collabs.length === 0) return t("vis.status.sharedNone");
    if (sharedWith.length === 0) {
      return t("vis.status.sharedAll").replace("{n}", String(collabs.length));
    }
    return t("vis.status.sharedSome").replace("{n}", String(sharedWith.length));
  }, [vis, sharedWith, collabs, t]);

  const toggleOrg = (id) => {
    setSharedWith((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Decide what happens when the user clicks an option card
  const pickOption = (v) => {
    // Trigger sell-or-give panel only for materials going public with no price set
    if (
      v === "public" &&
      resourceType === "material" &&
      (currentPrice == null || Number(currentPrice) <= 0) &&
      !priceConfirmed
    ) {
      setPricePrompt(true);
      setPriceMode(null);
      setPriceInput("");
      return;
    }
    setVis(v);
  };

  const confirmPriceChoice = async () => {
    if (priceMode === "giveaway") {
      // No backend write needed — price already 0
      setPriceConfirmed(true);
      setPricePrompt(false);
      setVis("public");
      return;
    }
    if (priceMode === "sell") {
      const n = Number(priceInput);
      if (!priceInput || !Number.isFinite(n) || n <= 0) {
        toast.error(t("vis.sell.invalidPrice"));
        return;
      }
      if (!onSetPrice) {
        toast.error(t("dash.failed"));
        return;
      }
      try {
        await onSetPrice(n);
        setPriceConfirmed(true);
        setPricePrompt(false);
        setVis("public");
      } catch (err) {
        toast.error(err.response?.data?.detail || t("dash.failed"));
      }
      return;
    }
    // No mode picked yet — toast prompt
    toast.error(t("vis.sell.pickOne"));
  };

  const cancelPricePrompt = () => {
    setPricePrompt(false);
    setPriceMode(null);
    setPriceInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/resources/${resourceType}/${resourceId}/visibility`, {
        visibility: vis,
        shared_with: vis === "shared" ? sharedWith : [],
      });
      toast.success(t("vis.saved"));
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setSaving(false); }
  };

  // Lookup map for status-row icon + tint colour based on current selection
  const currentTint = useMemo(() => {
    const o = OPTIONS.find((x) => x.key === vis);
    return o ? o.tint : "var(--ink-400)";
  }, [vis]);
  const CurrentIcon = useMemo(() => {
    const o = OPTIONS.find((x) => x.key === vis);
    return o ? o.Icon : Lock;
  }, [vis]);

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-4 sm:p-5" data-testid="visibility-picker">
      <div className="label-tech mb-1">{t("vis.title")}</div>
      <div className="text-xs text-[var(--ink-500)] mb-4">{t("vis.lead")}</div>

      {/* Three option cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {OPTIONS.map(({ key, Icon, tint }) => {
          const active = vis === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => pickOption(key)}
              data-testid={`vis-${key}-btn`}
              style={active ? {
                borderColor: tint,
                background: `color-mix(in srgb, ${tint} 14%, var(--surface))`,
                boxShadow: `inset 0 0 0 1px ${tint}`,
              } : undefined}
              className={`text-left p-3 border-2 transition-all relative ${
                active
                  ? ""
                  : "border-[var(--ink-200)] bg-[var(--surface)] hover:border-[var(--ink-400)]"
              }`}
            >
              {active && (
                <span
                  className="absolute top-1 right-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[var(--bone)]"
                  style={{ background: tint }}
                  data-testid={`vis-${key}-selected-tag`}
                >
                  {t("vis.selected")}
                </span>
              )}
              <div className="flex items-start gap-2">
                <span
                  className="inline-flex items-center justify-center w-9 h-9 shrink-0 transition-all"
                  style={
                    active
                      ? { background: tint, color: "var(--bone)" }
                      : { background: "var(--ink-100)", color: "var(--ink-400)" }
                  }
                >
                  <Icon size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold pr-12">{t(`vis.${key}.title`)}</div>
                  <div className="text-xs text-[var(--ink-500)] mt-1">{t(`vis.${key}.hint`)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Status summary — prominent, colour-matched to current selection */}
      <div
        className="mt-4 px-4 py-3 border-2 flex items-center gap-3"
        style={{
          borderColor: currentTint,
          background: `color-mix(in srgb, ${currentTint} 10%, var(--surface))`,
        }}
        data-testid="vis-status"
      >
        <span
          className="inline-flex items-center justify-center w-8 h-8 shrink-0"
          style={{ background: currentTint, color: "var(--bone)" }}
        >
          <CurrentIcon size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-500)]">
            {t("vis.status.label")}
          </div>
          <div className="text-sm font-semibold text-[var(--ink)]">{summary}</div>
          {vis === "shared" && (
            <div className="text-xs text-[var(--ink-500)] mt-0.5">{t("vis.shared.helper")}</div>
          )}
        </div>
      </div>

      {/* Sell-or-give inline panel (materials going public with no price) */}
      {pricePrompt && (
        <div className="mt-3 p-4 border-2 border-[var(--clay)] bg-[var(--surface-alt)]" data-testid="vis-price-prompt">
          <div className="flex items-start gap-2">
            <Globe size={16} className="mt-0.5 text-[var(--clay)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t("vis.sell.title")}</div>
              <div className="text-xs text-[var(--ink-500)] mt-1 mb-3">{t("vis.sell.question")}</div>

              {/* Always-visible segmented toggle: Sell / Give away */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPriceMode("sell")}
                  data-testid="vis-sell-btn"
                  className={`px-3 py-2 text-sm border-2 inline-flex items-center justify-center gap-2 transition-all ${
                    priceMode === "sell"
                      ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--bone)] font-semibold"
                      : "border-[var(--ink-200)] bg-[var(--surface)] hover:border-[var(--ink-400)]"
                  }`}
                >
                  <Tag size={13} /> {t("vis.sell.sell")}
                </button>
                <button
                  type="button"
                  onClick={() => { setPriceMode("giveaway"); setPriceInput(""); }}
                  data-testid="vis-giveaway-btn"
                  className={`px-3 py-2 text-sm border-2 inline-flex items-center justify-center gap-2 transition-all ${
                    priceMode === "giveaway"
                      ? "border-[var(--moss)] bg-[var(--moss)] text-[var(--bone)] font-semibold"
                      : "border-[var(--ink-200)] bg-[var(--surface)] hover:border-[var(--ink-400)]"
                  }`}
                >
                  <Gift size={13} /> {t("vis.sell.giveaway")}
                </button>
              </div>

              {/* Price input — ONLY when "Sell" is the active mode */}
              {priceMode === "sell" && (
                <div className="mt-3" data-testid="vis-sell-price-block">
                  <label className="label-tech mb-1 block">{t("vis.sell.priceLabel")}</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      autoFocus
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") confirmPriceChoice(); }}
                      className="input-tech flex-1"
                      placeholder="0"
                      data-testid="vis-sell-price-input"
                    />
                    <span className="self-center text-xs text-[var(--ink-500)]">{t("price.currency")}</span>
                  </div>
                </div>
              )}

              {/* Confirmation hint — when "Give away" is the active mode */}
              {priceMode === "giveaway" && (
                <div className="mt-3 px-3 py-2 bg-[var(--surface)] border border-[var(--ink-200)] text-xs text-[var(--ink-500)]" data-testid="vis-giveaway-hint">
                  {t("vis.sell.giveawayHint")}
                </div>
              )}

              {/* Confirm / Cancel — always visible at the bottom */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={confirmPriceChoice}
                  disabled={!priceMode || (priceMode === "sell" && !priceInput)}
                  className="px-3 py-1.5 text-xs font-semibold border border-[var(--ink)] bg-[var(--ink)] text-[var(--bone)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  data-testid="vis-pricedialog-confirm"
                >
                  <Check size={12} /> {t("vis.sell.confirm")}
                </button>
                <button
                  type="button"
                  onClick={cancelPricePrompt}
                  className="px-3 py-1.5 text-xs text-[var(--ink-500)] inline-flex items-center gap-1"
                  data-testid="vis-pricedialog-cancel"
                >
                  <X size={12} /> {t("vis.sell.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced (only when 'shared' AND user has collaborations) */}
      {vis === "shared" && collabs.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-xs text-[var(--ink-500)] hover:text-[var(--ink)] inline-flex items-center gap-1"
            data-testid="vis-advanced-toggle"
          >
            {advanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {t("vis.advanced.toggle")}
          </button>
          {advanced && (
            <div className="mt-2 p-3 border border-dashed border-[var(--ink-200)]">
              <div className="text-xs text-[var(--ink-500)] mb-2">{t("vis.advanced.hint")}</div>
              <div className="flex flex-wrap gap-2">
                {collabs.map((c) => {
                  const picked = sharedWith.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleOrg(c.id)}
                      data-testid={`vis-org-${c.id}`}
                      className={`px-3 py-1.5 text-xs border inline-flex items-center gap-1 ${
                        picked
                          ? "border-[var(--moss)] bg-[var(--moss)] text-[var(--bone)]"
                          : "border-[var(--ink-200)] bg-[var(--surface)]"
                      }`}
                    >
                      {picked && <Check size={11} />}
                      {c.name || c.id}
                    </button>
                  );
                })}
              </div>
              {sharedWith.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSharedWith([])}
                  className="mt-2 text-xs text-[var(--ink-500)] underline"
                  data-testid="vis-shared-clear"
                >
                  {t("vis.advanced.clear")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state: 'shared' chosen but no collaborations yet */}
      {vis === "shared" && collabs.length === 0 && (
        <div className="mt-3 px-3 py-2 border border-dashed border-[var(--ink-200)] text-xs text-[var(--ink-500)]">
          {t("vis.shared.noCollabs")}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="vis-save-btn">
          {saving ? "…" : t("vis.save")}
        </button>
      </div>
    </div>
  );
}
